import React, {
  useState,
  Fragment,
  useRef,
  useCallback,
  useEffect,
} from "react";
import { format } from "date-fns";
import html2canvas from "html2canvas";
import ReactDOM from "react-dom";
import { jsPDF } from "jspdf";
import PageHead from "../../components/pageHead";
import SearchDropdown from "../../components/searchDropdown";
import Table from "../../components/table";
import DownloadIcon from "../../icons/downloadIcon";
import DateRange from "../../components/dateRange";
import ReportCard from "../../components/reportCard";
import SelectInput from "../../components/selectInput";
import useMediaQuery from "../../hooks/useMediaQuery";
// import { checkScrollDirection } from "../../utils/menuNav";
import Pagination from "../../components/pagination";
import CloseIcon from "../../icons/closeIcon";
import PDFDownloadIcon from "../../icons/pdfDownloadIcon";
import MobileNav from "../../components/mobileNav";
import { useAxiosPrivate } from "../../hooks/useAxiosPrivate";
import CardSkeleton from "../../components/cardSkeleton";
import TutorialReportsImage from "../../assets/images/tutorialReports.png";
import NoData from "../../components/noData";
import PrintPDF from "./printPDF";
import styles from "./style.module.css";
import stylesTable from "../../components/table/style.module.css";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import PrintIcon from "../../icons/printIcon";

const categoryOptions = [
  { value: "all", label: "All Maintenance" },
  { value: "consumables", label: "Consumables Only" },
];

// Update the PDF.js worker configuration
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`;

const Report = () => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const targetRef = useRef();
  const axiosPrivate = useAxiosPrivate();
  const [downloadLoading, setDownloadLoading] = useState(false);

  const generatePDF = async () => {
    setDownloadLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      const options = {
        overrides: {
          pdf: {},
          canvas: {},
        },
        page: {
          format: "a4",
          orientation: "portrait",
          margin: 10,
        },
        resolution: 4,
        canvas: {
          mimeType: "image/jpeg",
          qualityRatio: 1,
        },
      };

      const doc = new jsPDF({
        format: options.page.format,
        orientation: options.page.orientation,
      });

      const canvas = await html2canvas(targetRef.current, {
        scale: options.resolution,
        width: 1440,
        height: targetRef.current.scrollHeight + 100,
        windowWidth: 1440,
      });

      const imgData = canvas.toDataURL(
        options.canvas.mimeType,
        options.canvas.qualityRatio
      );

      const pdfWidth = doc.internal.pageSize.getWidth();
      const pdfHeight = doc.internal.pageSize.getHeight();

      // Calculate dimensions while maintaining aspect ratio
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(
        (pdfWidth - options.page.margin * 2) / imgWidth,
        (pdfHeight - options.page.margin * 2) / imgHeight
      );

      const scaledWidth = imgWidth * ratio;
      const scaledHeight = imgHeight * ratio;

      // Center the image
      const x =
        options.page.margin +
        (pdfWidth - options.page.margin * 2 - scaledWidth) / 2;
      doc.addImage(imgData, "PNG", x, 10, scaledWidth, scaledHeight);
      doc.setDisplayMode("fullwidth");
      doc.setProperties({
        title: "DashBook Report",
      });

      // Get the PDF as a blob
      const pdfBlob = doc.output("blob");
      setPdfData(pdfBlob);
      setDownloadLoading(false);
    } catch (error) {
      console.error("Error generating PDF:", error);
      setDownloadLoading(false);
    }
  };

  const footerCol = (
    name,
    totalConsumable = 0,
    total = 0,
    hide = true,
    type = "all"
  ) => {
    switch (name) {
      case "date":
        return hide ? (
          <span>&nbsp;</span>
        ) : (
          <div
            className={`${stylesTable.table_footer_link} print_hide`}
            onClick={generatePDF}
          >
            <DownloadIcon />
            <span>Download Printable Version</span>
          </div>
        );
      case "description":
        return (
          <div className={`${stylesTable.table_footer_text} tft-center`}>
            Total Consumables: {totalConsumable}
          </div>
        );
      case "cost":
        return type === "all" ? (
          <div
            className={`${stylesTable.table_footer_text} ${stylesTable.table_footer_text_left} `}
          >
            Total Service: {total}
          </div>
        ) : (
          ""
        );
      default:
        return "";
    }
  };

  const [mainColumns, setMainColumns] = useState({
    columns: [
      {
        name: "date",
        title: "Date",
        width: 150,
        sorting: true,
        direction: "",
        colSpan: 2,
        footer_col: footerCol("date"),
      },
      {
        name: "mileage",
        title: "Mileage",
        width: 110,
        sorting: true,
        direction: "",
        colSpan: 1,
        footer_col: "",
      },
      {
        name: "consumable",
        title: "Category",
        width: 200,
        sorting: true,
        direction: "",
        colSpan: 1,
      },
      {
        name: "description",
        title: "Description",
        width: 350,
        sorting: true,
        direction: "",
        colSpan: 2,
        footer_col: footerCol("description", "$0", ""),
      },
      {
        name: "source",
        title: "Source",
        width: 200,
        sorting: true,
        direction: "",
      },
      {
        name: "cost",
        title: "Cost",
        width: 100,
        sorting: true,
        direction: "",
        colSpan: 2,
        footer_col: footerCol("cost", "", "$0"),
      },
    ],
  });

  const [footerColumns, setFooterColumns] = useState({
    columns: [
      {
        name: "date",
        colSpan: 2,
        footer_col: footerCol("date"),
      },
      {
        name: "mileage",
        colSpan: 1,
        footer_col: "",
      },
      {
        name: "consumable",
        colSpan: 1,
        footer_col: "",
      },
      {
        name: "description",
        colSpan: 2,
        footer_col: footerCol("description", "$0", "", "all"),
      },
      {
        name: "source",
        colSpan: 1,
        footer_col: "",
      },
      {
        name: "cost",
        colSpan: 2,
        footer_col: footerCol("cost", "", "$0", "all"),
      },
    ],
  });

  const [rowData, setRowData] = useState([]);
  const [apiParams] = useState({});
  const [selectedCategory, setSelectedCategory] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedPage, setSelectedPage] = useState(1);
  const [limitData] = useState(10);
  const [isScrolling] = useState(false);
  const [totalData, setTotalData] = useState(0);
  const [hideConsumable] = useState(false);
  const [consumables, setConsumables] = useState([]);
  const [totalConsumable, setTotalConsumable] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [dateRangeValue, setDateRangeValue] = useState({});
  const [selectedCat, setSelectedCat] = useState(categoryOptions[0]);
  const [pdfData, setPdfData] = useState(null);

  const handleCats = (value) => {
    setSelectedCat(value);
    let prm = apiParams;
    prm.type = value.value;
    fetchReport(prm);
  };

  const handleSorting = (name, index) => {
    let prm = apiParams;
    let tempCol = mainColumns.columns;
    if (tempCol[index].direction === "") {
      tempCol.map((column) =>
        column.name === name
          ? (column.direction = "ASC")
          : (column.direction = "")
      );
      prm.sort_by = name;
      prm.order = 1;
      prm.page = 1;
    } else if (tempCol[index].direction === "ASC") {
      tempCol.map((column) =>
        column.name === name
          ? (column.direction = "DSC")
          : (column.direction = "")
      );
      prm.sort_by = name;
      prm.order = -1;
      prm.page = 1;
    } else if (tempCol[index].direction === "DSC") {
      tempCol.map((column) => (column.direction = ""));
      prm.page = 1;
      delete prm.sort_by;
      delete prm.order;
    }
    setMainColumns({ columns: tempCol });
    setSelectedPage(1);
    fetchReport(apiParams);
  };

  function generateRows(tempArray) {
    const tempRowArray = [];
    if (tempArray) {
      tempArray.map((item) =>
        tempRowArray.push({
          date: (
            <div className={stylesTable.table_cell_text}>
              {format(new Date(item.date.replace("Z", "")), "MMMM dd, yyyy")}
            </div>
          ),
          mileage: (
            <div className={stylesTable.table_cell_text}>{item.mileage}</div>
          ),
          consumable: (
            <div className={stylesTable.table_cell_text}>
              {item?.consumable?.name || "N/A - Non Consumable"}
            </div>
          ),
          description: (
            <div className={stylesTable.table_cell_text}>
              {item.description}
            </div>
          ),
          source: (
            <div className={stylesTable.table_cell_text}>{item.source}</div>
          ),
          cost: (
            <div className={stylesTable.table_cell_text}>
              {item.cost ? "$" + item.cost : ""}
            </div>
          ),
        })
      );
    }
    return tempRowArray;
  }

  function generateFooterColumns(tempArray) {
    const tempFooterArray = [];
    if (tempArray) {
      tempArray.map((item) =>
        tempFooterArray.push({
          col: item.footer_col || "",
          colSpan: item.colSpan || 1,
        })
      );
    }
    return tempFooterArray;
  }

  const handlePaginate = (value) => {
    let prm = apiParams;
    prm.page = value;
    setSelectedPage(value);
    setLoading(true);
    fetchReport(prm);
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(JSON.parse(value));
    let prm = apiParams;
    prm.consumable = JSON.parse(value).value;
    fetchReport(prm);
  };

  const handleDateChange = (value) => {
    let prm = apiParams;
    if (value.selection.startDate && value.selection.endDate) {
      let startDate = format(value.selection.startDate, "MM-dd-yyyy");
      let endDate = format(value.selection.endDate, "MM-dd-yyyy");
      prm.startDate = startDate;
      prm.endDate = endDate;
      setDateRangeValue({
        startDate: format(value.selection.startDate, "dd MMM yyyy"),
        endDate: format(value.selection.endDate, "dd MMM yyyy"),
      });
    } else {
      delete prm.startDate;
      delete prm.endDate;
      setDateRangeValue({});
    }
    fetchReport(prm);
  };

  const handleScroll = (e) => {
    // const scrollDirection = checkScrollDirection(e);
    // setIsScrolling(scrollDirection);
  };

  const fetchConsumables = useCallback(async () => {
    try {
      let url = `consumables/?page=1&limit=100`;
      let options = {
        method: "GET",
        url,
      };
      await axiosPrivate(options)
        .then((response) => {
          if (response.data.status === 1) {
            const consumables = response.data.consumables;
            const consumableOptions = consumables
              .filter((item) => item.status === "active")
              .map((item) => ({
                value: item._id,
                title: item.name,
              }));
            setConsumables(consumableOptions);
          }
        })
        .catch((err) => {
          if (err.response) {
            // console.error('err.res', err.response.data);
          }
        });
    } catch (error) {
      // console.error('error', error);
    }
    // eslint-disable-next-line
  }, []);

  const fetchReport = useCallback(async (params) => {
    setLoading(true);
    try {
      let urlParams = "";
      if (params) {
        Object.keys(params).forEach(function (key, index) {
          urlParams += (index === 0 ? "?" : "&") + key + "=" + params[key];
        });
      }
      let url = `report${urlParams}`;
      let options = {
        method: "GET",
        url,
      };
      await axiosPrivate(options)
        .then((response) => {
          if (response.data.status === 1) {
            let totalConsumable = response.data.totalConsumable;
            let totalCost = response.data.totalCost;
            let totalService = response.data.total;
            let tempCol = [...footerColumns.columns];
            let type = params?.type || "all";
            setRowData(response.data.services);
            setTotalData(totalService);
            setTotalConsumable(totalConsumable);
            setTotalCost(totalCost);

            const cols = tempCol.map((item) => {
              return {
                name: item.name,
                colSpan: item.colSpan,
                footer_col: footerCol(
                  item.name,
                  `$${totalConsumable.toLocaleString()}`,
                  `$${totalCost.toLocaleString()}`,
                  totalService === 0,
                  type
                ),
              };
            });
            setFooterColumns({ columns: cols });
            setLoading(false);
          } else {
            setRowData([]);
            setTotalData(0);
            setLoading(false);
          }
        })
        .catch((err) => {
          if (err.response) {
            setLoading(false);
            // console.error('err.res', err.response.data);
          }
        });
    } catch (error) {
      setLoading(false);
      // console.error('error', error);
    }
    // eslint-disable-next-line
  }, []);

  const handleClosePDF = () => {
    setPdfData(null);
    let iframe = document.querySelectorAll(".print_iframe_hide");
    if (iframe) {
      iframe.forEach((item) => {
        item.remove();
      });
    }
  };

  const handleDownload = () => {
    if (pdfData) {
      const url = URL.createObjectURL(pdfData);
      const link = document.createElement("a");
      link.href = url;
      link.download = "DashBook_Report.pdf";
      link.click();
      URL.revokeObjectURL(url);
    }
  };

  const handlePrint = () => {
    if (pdfData) {
      const url = URL.createObjectURL(pdfData);
      document.querySelectorAll(".print_iframe_hide").forEach((item) => {
        item.remove();
      });
      const iframe = document.createElement("iframe");
      iframe.classList.add("print_iframe_hide");
      iframe.style.display = "none";
      iframe.style.border = "none";
      document.body.appendChild(iframe);

      // Add print styles
      const style = document.createElement("style");
      style.textContent = `
        @media print {
          body > *:not(.print_iframe_hide) {
            display: none !important;
          }
          .print_iframe_hide {
            position: static !important;
            width: 100% !important;
            height: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
            display: block !important;
          }
          @page {
            margin: 0;
            size: auto;
          }
        }
      `;
      document.head.appendChild(style);

      iframe.onload = () => {
        try {
          iframe.contentWindow.print();
        } catch (error) {
          console.error("Print failed:", error);
        }
      };

      iframe.src = url;
    }
  };

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    if (hideConsumable) {
      fetchConsumables();
    }
  }, [fetchConsumables, hideConsumable]);

  return (
    <Fragment>
      <PrintPDF
        reference={targetRef}
        rows={generateRows(rowData)}
        columns={mainColumns.columns}
        footerColumns={generateFooterColumns(footerColumns.columns)}
        selectedPage={selectedPage}
        handlePagination={handlePaginate}
        loading={loading}
        handleSort={handleSorting}
        limitData={limitData}
        totalData={rowData.length}
        downloadLoading={downloadLoading}
        dateRangeValue={dateRangeValue}
        selectedCategory={selectedCategory}
      />
      {pdfData &&
        ReactDOM.createPortal(
          <div className={styles.pdf_viewer}>
            <div className={styles.pdf_viewer_wrapper}>
              <div className={styles.pdf_actions}>
                <div
                  className={styles.pdf_action_btn}
                  type="button"
                  onClick={handlePrint}
                  title="Print PDF"
                >
                  <PrintIcon />
                </div>
                <div
                  className={styles.pdf_action_btn}
                  type="button"
                  onClick={handleDownload}
                  title="Download PDF"
                >
                  <PDFDownloadIcon />
                </div>
                <div
                  className={styles.pdf_action_btn}
                  type="button"
                  onClick={handleClosePDF}
                  title="Close PDF"
                >
                  <CloseIcon />
                </div>
              </div>
              <Document
                file={pdfData}
                loading={
                  <div className={styles.pdf_loading}>
                    <div className={styles.pdf_loading_text}>
                      Loading PDF...
                    </div>
                  </div>
                }
                error={
                  <div className={styles.pdf_error}>Failed to load PDF.</div>
                }
                className={styles.pdf_document}
                options={{
                  cMapUrl:
                    "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/cmaps/",
                  cMapPacked: true,
                  standardFontDataUrl:
                    "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/standard_fonts/",
                }}
              >
                <Page
                  pageNumber={1}
                  width={Math.min(window.innerWidth * 0.8, 800)}
                  className={styles.pdf_page}
                />
              </Document>
            </div>
          </div>,
          document.body
        )}
      <PageHead
        logo={isMobile}
        title={"Report"}
        tutorialContent={
          <Fragment>
            <p>
              The <strong>Reports Page</strong> provides an overview of vehicle
              expenses. You can select the date range from Year to Date, Last 12
              Months, or set a custom date range.
            </p>
            <p>
              Then, filter your report by <strong>Consumables Only</strong> or
              <strong>All Maintenance Expenses</strong>.
            </p>
            <p>
              Your total expenses will be displayed at the bottom of the page
              and you can download a printable report via the link at the bottom
              of the page.
            </p>
            <img src={TutorialReportsImage} alt={"Reports Page"} />
          </Fragment>
        }
      />
      <div className={styles.body}>
        <div className={styles.body_head}>
          <div className={styles.body_head_inner}>
            <div className={styles.filters}>
              <DateRange
                customClass={styles.date_filters}
                onDateChange={handleDateChange}
              />
              {hideConsumable && (
                <SearchDropdown
                  schema={{
                    options: consumables,
                    placeholder: "Select Category",
                    customClass: styles.category_filters,
                    clearable: true,
                  }}
                  value={JSON.stringify(selectedCategory)}
                  onChange={handleCategoryChange}
                />
              )}
              <SelectInput
                options={{
                  options: categoryOptions,
                  customClass: styles.category_filters,
                }}
                value={JSON.stringify(selectedCat)}
                isFormField={false}
                onChange={handleCats}
              />
            </div>
          </div>
        </div>
        <div className={styles.body_view} onScroll={handleScroll}>
          {isMobile ? (
            <Fragment>
              {rowData.map((item, index) => (
                <ReportCard key={index} item={item} />
              ))}
              {loading && <CardSkeleton />}
              {totalData === 0 && !loading && <NoData />}
            </Fragment>
          ) : (
            <Table
              rows={generateRows(rowData)}
              columns={mainColumns.columns}
              footerColumns={generateFooterColumns(footerColumns.columns)}
              selectedPage={selectedPage}
              handlePagination={handlePaginate}
              loading={loading}
              handleSort={handleSorting}
              limitData={limitData}
              totalData={totalData}
            />
          )}
        </div>
        {isMobile && (
          <div className={styles.extra_info}>
            <div className={styles.extra_info_left}>
              {totalData !== 0 && (
                <div onClick={generatePDF} className={styles.extra_info_btn}>
                  <DownloadIcon />
                  <span>Download Printable Version</span>
                </div>
              )}
            </div>
            <div className={styles.extra_info_right}>
              {selectedCat.value === "all" && (
                <div className={styles.extra_info_text}>
                  Total Service: ${totalCost.toLocaleString()}
                </div>
              )}
              <div className={styles.extra_info_text}>
                Total Consumables: ${totalConsumable.toLocaleString()}
              </div>
            </div>
          </div>
        )}
        {isMobile && (
          <Fragment>
            {totalData > limitData && (
              <div className={styles.body_foot}>
                <Pagination
                  totalPages={totalData}
                  limitData={limitData}
                  selectedPage={selectedPage}
                  handlePagination={handlePaginate}
                />
              </div>
            )}
          </Fragment>
        )}
      </div>
      {isMobile && <MobileNav defaultActive={true} isScrolling={isScrolling} />}
    </Fragment>
  );
};

export default Report;
